import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fmtNumber, fmtDate } from "@/lib/api";
import { downloadXlsx, todayStamp } from "@/lib/xlsx";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUp, ArrowDown, CircleNotch, ArrowsClockwise, Sliders, DownloadSimple } from "@phosphor-icons/react";
import { DateField } from "@/components/ui/date-field";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Movimientos() {
  const [lotes, setLotes] = useState([]);
  const [movs, setMovs] = useState(null);
  const [loteId, setLoteId] = useState("");
  const [tipo, setTipo] = useState("salida");     // ingreso | salida | ajuste
  const [efecto, setEfecto] = useState("entrada"); // para ajuste
  const [cantidad, setCantidad] = useState("");
  const [motivo, setMotivo] = useState("");
  const [fecha, setFecha] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toReverso, setToReverso] = useState(null);
  const [loteSearch, setLoteSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("todos");
  const [filterProductoId, setFilterProductoId] = useState("todos");
  const [filterLoteId, setFilterLoteId] = useState("todos");

  const loadMovs = useCallback(() => {
    const params = new URLSearchParams({ limit: "200" });
    if (filterTipo !== "todos") params.set("tipo", filterTipo);
    if (filterLoteId !== "todos") params.set("lote_id", filterLoteId);
    else if (filterProductoId !== "todos") params.set("producto_id", filterProductoId);
    setMovs(null);
    api.get(`/movimientos?${params.toString()}`).then((r) => setMovs(r.data));
  }, [filterTipo, filterProductoId, filterLoteId]);

  const loadLotes = useCallback(() => {
    api.get("/lotes").then((r) => setLotes(r.data));
  }, []);

  useEffect(() => { loadLotes(); }, [loadLotes]);
  useEffect(() => { loadMovs(); }, [loadMovs]);

  const productosFilter = Array.from(
    new Map(lotes.map((l) => [l.producto_id, { id: l.producto_id, nombre: l.nombre, codigo: l.codigo }])).values()
  ).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));

  const lotesFilter = filterProductoId === "todos"
    ? lotes
    : lotes.filter((l) => l.producto_id === filterProductoId);

  const sugerirLote = () => {
    const conStock = lotes.filter((l) => l.stock_actual > 0);
    if (conStock.length === 0) return;
    const sortable = (l) =>
      l.dias_hasta_vencimiento === null || l.dias_hasta_vencimiento === undefined
        ? Infinity
        : l.dias_hasta_vencimiento;
    const oldest = [...conStock].sort((a, b) => sortable(a) - sortable(b))[0];
    setLoteId(oldest.id);
    toast.info(`Sugerido: ${oldest.nombre} · ${oldest.numero_lote}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!loteId) {
      setError("Selecciona un lote.");
      return;
    }
    if (tipo === "ajuste" && !motivo.trim()) {
      setError("El motivo es obligatorio para un ajuste.");
      return;
    }
    setSaving(true);
    try {
      if (tipo === "ajuste") {
        await api.post("/movimientos/ajuste", {
          lote_id: loteId,
          efecto,
          cantidad: parseInt(cantidad, 10),
          motivo,
          ...(fecha ? { fecha_movimiento: fecha } : {}),
        });
        toast.success("Ajuste registrado");
      } else {
        await api.post("/movimientos", {
          lote_id: loteId,
          tipo,
          cantidad: parseInt(cantidad, 10),
          motivo,
          ...(fecha ? { fecha_movimiento: fecha } : {}),
        });
        toast.success(tipo === "ingreso" ? "Ingreso registrado" : "Salida registrada");
      }
      setCantidad("");
      setMotivo("");
      setFecha("");
      loadLotes();
      loadMovs();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedLote = lotes.find((l) => l.id === loteId);

  const filteredLotes = lotes.filter((l) => {
    const s = loteSearch.trim().toLowerCase();
    if (!s) return true;
    return (
      (l.numero_lote || "").toLowerCase().includes(s) ||
      (l.nombre || "").toLowerCase().includes(s) ||
      (l.codigo || "").toLowerCase().includes(s)
    );
  });

  const doReverso = async () => {
    try {
      await api.post(`/movimientos/${toReverso.id}/reverso`);
      toast.success("Movimiento revertido");
      setToReverso(null);
      loadLotes();
      loadMovs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al revertir");
    }
  };

  const tipoLabel = (m) => {
    if (m.tipo === "ingreso") return "Ingreso";
    if (m.tipo === "salida") return "Salida";
    if (m.doc_tipo === "reverso") return "Reverso";
    return `Ajuste ${m.efecto === "entrada" ? "+" : "−"}`;
  };
  const tipoColor = (m) => {
    if (m.tipo === "ingreso") return "text-[#065F46]";
    if (m.tipo === "salida") return "text-[#991B1B]";
    return "text-[#1D4ED8]";
  };
  const esEntrada = (m) => m.tipo === "ingreso" || (m.tipo === "ajuste" && m.efecto === "entrada");
  const reversible = (m) => m.tipo !== "ajuste" && !m.reverso_de;

  const exportExcel = () => {
    const toDate = (iso) => {
      const p = (iso || "").slice(0, 10).split("-");
      return p.length === 3 ? new Date(+p[0], +p[1] - 1, +p[2]) : iso;
    };
    const rows = [["Fecha", "Producto", "Lote", "Tipo", "Cantidad", "Costo unitario", "Costo total", "Usuario", "Motivo"]];
    (movs || []).forEach((m) =>
      rows.push([
        toDate(m.fecha_movimiento), m.lote_nombre, m.numero_lote, tipoLabel(m), m.cantidad,
        m.costo_unitario ?? "", m.costo_total ?? "", m.usuario ?? "", m.motivo,
      ]));
    downloadXlsx(`movimientos_${todayStamp()}.xlsx`, [
      { name: "Movimientos", rows, cols: [{ wch: 12 }, { wch: 24 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 20 }, { wch: 24 }] },
    ]);
  };

  const tipoBtn = (val, label, Icon, activeCls) => (
    <button
      type="button"
      data-testid={`tipo-${val}`}
      onClick={() => setTipo(val)}
      className={`flex items-center justify-center gap-1.5 h-11 rounded-md text-sm font-semibold border transition-all duration-200 ${
        tipo === val ? activeCls : "bg-white border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
      }`}
    >
      <Icon size={16} weight="bold" /> {label}
    </button>
  );

  return (
    <div className="space-y-6 animate-fade-up" data-testid="movimientos-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Movimientos</h1>
          <p className="text-[#475569] mt-1">Ledger inmutable: registra ingresos, salidas y ajustes. Las correcciones se hacen con ajustes o reversos.</p>
        </div>
        <Button data-testid="export-movimientos-excel" variant="outline" onClick={exportExcel} disabled={!movs || movs.length === 0} className="h-11 border-[#E2E8F0] font-semibold">
          <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Exportar Excel
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="bg-white border border-[#E2E8F0] rounded-md p-6 h-fit">
          <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-4">Nuevo movimiento</h2>
          <form onSubmit={submit} className="space-y-4" data-testid="movimiento-form">
            <div className="grid grid-cols-3 gap-2">
              {tipoBtn("ingreso", "Ingreso", ArrowDown, "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]")}
              {tipoBtn("salida", "Salida", ArrowUp, "bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]")}
              {tipoBtn("ajuste", "Ajuste", Sliders, "bg-[#EFF6FF] border-[#BFDBFE] text-[#1D4ED8]")}
            </div>

            {tipo === "ajuste" && (
              <div className="grid grid-cols-2 gap-2" data-testid="ajuste-efecto-group">
                <button
                  type="button"
                  data-testid="efecto-entrada"
                  onClick={() => setEfecto("entrada")}
                  className={`h-10 rounded-md text-sm font-semibold border transition-all ${efecto === "entrada" ? "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]" : "bg-white border-[#E2E8F0] text-[#475569]"}`}
                >
                  + Sumar stock
                </button>
                <button
                  type="button"
                  data-testid="efecto-salida"
                  onClick={() => setEfecto("salida")}
                  className={`h-10 rounded-md text-sm font-semibold border transition-all ${efecto === "salida" ? "bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]" : "bg-white border-[#E2E8F0] text-[#475569]"}`}
                >
                  − Restar stock
                </button>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Lote</Label>
                {tipo === "salida" && (
                  <button type="button" onClick={sugerirLote} data-testid="sugerir-lote" className="text-xs font-semibold text-[#0F172A] underline underline-offset-2">
                    Sugerir (vence primero)
                  </button>
                )}
              </div>
              <Input
                data-testid="lote-search-input"
                value={loteSearch}
                onChange={(e) => setLoteSearch(e.target.value)}
                placeholder="Filtrar por nº de lote o producto..."
                className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A] mb-2"
              />
              <Select value={loteId} onValueChange={setLoteId}>
                <SelectTrigger data-testid="lote-select" className="bg-white border-[#E2E8F0]">
                  <SelectValue placeholder="Selecciona un lote" />
                </SelectTrigger>
                <SelectContent>
                  {filteredLotes.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-[#64748B]" data-testid="lote-select-empty">Sin coincidencias</div>
                  ) : (
                    filteredLotes.map((l) => (
                      <SelectItem key={l.id} value={l.id} data-testid={`lote-option-${l.id}`}>
                        {l.nombre} · {l.numero_lote} (stock {fmtNumber(l.stock_actual)})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedLote && (
                <p className="text-xs text-[#64748B]">Stock disponible: <span className="font-semibold text-[#0F172A]">{fmtNumber(selectedLote.stock_actual)}</span></p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Cantidad</Label>
              <Input
                data-testid="cantidad-input"
                type="number"
                min={1}
                required
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                placeholder="0"
                className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">
                Motivo {tipo === "ajuste" && <span className="text-[#991B1B]">*</span>}
              </Label>
              <Input
                data-testid="motivo-input"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder={tipo === "ajuste" ? "Corrección de inventario, conteo físico..." : tipo === "ingreso" ? "Compra, reposición..." : "Venta, uso interno, merma..."}
                className="bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Fecha del movimiento (opcional)</Label>
              <DateField value={fecha} onChange={setFecha} testid="fecha-input" />
              <p className="text-xs text-[#94A3B8]">Déjalo vacío para usar la fecha de hoy. Puedes registrar fechas pasadas.</p>
            </div>

            {error && (
              <p data-testid="movimiento-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button
              type="submit"
              data-testid="movimiento-submit"
              disabled={saving}
              className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold"
            >
              {saving ? <CircleNotch size={18} className="animate-spin" /> : "Registrar movimiento"}
            </Button>
          </form>
        </div>

        {/* History */}
        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md">
          <div className="px-6 py-4 border-b border-[#E2E8F0] space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-bold text-[#0F172A]">Historial reciente</h2>
              {movs !== null && (
                <span className="text-xs font-semibold text-[#64748B]" data-testid="movimientos-count">
                  {movs.length} movimiento{movs.length === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="grid grid-cols-4 gap-1 bg-[#F1F5F9] p-1 rounded-md shrink-0" data-testid="filter-tipo-group">
                {[
                  { v: "todos", label: "Todos" },
                  { v: "ingreso", label: "Ingresos" },
                  { v: "salida", label: "Salidas" },
                  { v: "ajuste", label: "Ajustes" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    data-testid={`filter-tipo-${opt.v}`}
                    onClick={() => setFilterTipo(opt.v)}
                    className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                      filterTipo === opt.v ? "bg-white text-[#0F172A] shadow-sm" : "text-[#64748B] hover:text-[#0F172A]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <Select
                value={filterProductoId}
                onValueChange={(v) => { setFilterProductoId(v); setFilterLoteId("todos"); }}
              >
                <SelectTrigger data-testid="filter-producto-select" className="bg-white border-[#E2E8F0] sm:w-48">
                  <SelectValue placeholder="Producto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos" data-testid="filter-producto-todos">Todos los productos</SelectItem>
                  {productosFilter.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`filter-producto-${p.id}`}>
                      {p.nombre}{p.codigo ? ` · ${p.codigo}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterLoteId} onValueChange={setFilterLoteId}>
                <SelectTrigger data-testid="filter-lote-select" className="bg-white border-[#E2E8F0] sm:w-48">
                  <SelectValue placeholder="Lote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos" data-testid="filter-lote-todos">Todos los lotes</SelectItem>
                  {lotesFilter.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`filter-lote-${l.id}`}>
                      {l.numero_lote} · {l.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {movs === null ? (
            <div className="flex items-center justify-center py-20">
              <CircleNotch size={28} className="animate-spin text-[#0F172A]" />
            </div>
          ) : movs.length === 0 ? (
            <p className="px-6 py-12 text-center text-[#64748B]" data-testid="movimientos-empty">
              {filterTipo !== "todos" || filterProductoId !== "todos" || filterLoteId !== "todos"
                ? "Sin movimientos para los filtros seleccionados."
                : "Sin movimientos."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="movimientos-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                    <th className="px-6 py-3 font-semibold">Lote</th>
                    <th className="px-6 py-3 font-semibold">Tipo</th>
                    <th className="px-6 py-3 font-semibold">Motivo</th>
                    <th className="px-6 py-3 font-semibold text-right">Cant.</th>
                    <th className="px-6 py-3 font-semibold text-right">Fecha</th>
                    <th className="px-6 py-3 font-semibold text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {movs.map((m) => (
                    <tr key={m.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                      <td className="px-6 py-3">
                        <div className="font-medium text-[#0F172A]">{m.lote_nombre}</div>
                        <div className="text-xs text-[#64748B]">{m.numero_lote}</div>
                      </td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold ${tipoColor(m)}`}>
                          {esEntrada(m) ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" />}
                          {tipoLabel(m)}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-[#475569]">{m.motivo}</td>
                      <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(m.cantidad)}</td>
                      <td className="px-6 py-3 text-right text-[#64748B]">{fmtDate(m.fecha_movimiento)}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {reversible(m) ? (
                            <button
                              data-testid={`reverso-mov-${m.id}`}
                              onClick={() => setToReverso(m)}
                              className="p-2 rounded-md text-[#475569] hover:bg-[#EFF6FF] hover:text-[#1D4ED8] transition-colors"
                              title="Revertir"
                            >
                              <ArrowsClockwise size={16} />
                            </button>
                          ) : (
                            <span className="text-xs text-[#CBD5E1]">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Reverso confirm */}
      <AlertDialog open={Boolean(toReverso)} onOpenChange={(o) => !o && setToReverso(null)}>
        <AlertDialogContent data-testid="reverso-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Revertir este movimiento?</AlertDialogTitle>
            <AlertDialogDescription>
              Se creará un movimiento de ajuste contrario por {fmtNumber(toReverso?.cantidad || 0)} unidades.
              El movimiento original se conserva en el historial (ledger inmutable).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reverso-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="reverso-confirm" onClick={doReverso} className="bg-[#1D4ED8] hover:bg-[#1E40AF]">
              Revertir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
